// libs
import copy from 'clipboard-copy'
import { useCallback, useMemo } from 'react'

// interfaces / constants
import { ITrackingType } from 'src/high_order_components/with_tracker_props'
import {
  HeaderDetailsObjType,
  HeaderGroupObjType,
  PostDetailsObjType,
  PostPreviewObjType,
  SHARE_LINK,
  SHARE_NATIVE
} from 'src/utils/reporting/events_tracking/events_tracking'

// components
import { showSnackBar } from 'src/components/snack_bar/snack_bar'

// helpers
import {
  buildShareTargets,
  ISharePayload,
  shareTexts,
  ShareTrackingAction
} from 'src/components/share/lib/share_targets'
import { shareNatively, toAbsoluteUrl, withUTMParams } from 'src/components/share/lib/utils'
import { reportError } from 'src/utils/reporting/report-errors'

export type AllowedShareTracking =
  ITrackingType<HeaderDetailsObjType> |
  ITrackingType<HeaderGroupObjType> |
  ITrackingType<PostDetailsObjType> |
  ITrackingType<PostPreviewObjType>;

export interface IShareOptions extends ISharePayload {
  /** frontend path of the shared page, e.g. `/articles/<slug>` */
  url: string;
  utmSource: string;
  tracking?: AllowedShareTracking;
}

export const useShare = ({ url, utmSource, title, text, tracking }: IShareOptions) => {
  const absoluteUrl = toAbsoluteUrl(url)

  const targets = useMemo(
    () => buildShareTargets(absoluteUrl, utmSource, { text, title }),
    [absoluteUrl, utmSource, text, title]
  )

  const track = useCallback((action: ShareTrackingAction) => {
    if (tracking) {
      tracking.tracker(action, tracking.trackingObj.LABELS.FINISH, undefined, { shareUrl: absoluteUrl })
    }
  }, [tracking, absoluteUrl])

  const copyLink = useCallback(() => {
    const link = withUTMParams(absoluteUrl, { medium: 'link', source: utmSource })
    copy(link)
      .then(() => {
        track(SHARE_LINK)
        showSnackBar(shareTexts.linkWasCopiedToClipboard)
      })
      .catch((error) => {
        reportError('error on copying share link to clipboard', { error, link })
      })
  }, [absoluteUrl, utmSource, track])

  const openShareSheet = useCallback((onFallback: () => void) => shareNatively(
    { text, title: title ?? undefined, url: withUTMParams(absoluteUrl, { medium: 'native-web', source: utmSource }) },
    { onFallback, onShared: () => track(SHARE_NATIVE) }
  ), [absoluteUrl, utmSource, text, title, track])

  return { copyLink, openShareSheet, targets, track }
}
